import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { User, MapPin, Phone, Calendar, ArrowRight, PencilSimple, Heart, Trophy } from '@phosphor-icons/react';
import SEO from '../components/SEO';
import ListState from '../components/ListState';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PlayerDashboard = () => {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [saved, setSaved] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/profile`, { withCredentials: true }).then(r => setProfile(r.data)).catch(() => {}),
      axios.get(`${API}/registrations/my`, { withCredentials: true }).then(r => setRegistrations(r.data)).catch(() => {}),
      axios.get(`${API}/saved-tournaments`, { withCredentials: true }).then(r => setSaved(r.data)).catch(() => {})
    ]).finally(() => setLoading(false));
  }, []);

  const statusBadge = (status) => {
    const styles = {
      approved: 'bg-green-50 text-green-700 border-green-200',
      rejected: 'bg-red-50 text-red-700 border-red-200',
      pending_verification: 'bg-orange-50 text-orange-700 border-orange-200',
      form_submitted: 'bg-orange-50 text-orange-700 border-orange-200'
    };
    return styles[status] || styles.pending_verification;
  };

  const statusLabel = (status) => {
    const map = {
      approved: 'Approved',
      rejected: 'Rejected',
      pending_verification: 'Pending Verification',
      form_submitted: 'Pending Verification'
    };
    return map[status] || 'Pending';
  };

  const fields = ['name', 'phone', 'city', 'gender', 'dob', 'current_team', 'position', 'height'];
  const completion = profile ? Math.round((fields.filter(f => profile[f]?.trim?.()).length / fields.length) * 100) : 0;

  return (
    <>
      <SEO title="My Dashboard" description="Manage your basketball profile, track tournament registrations, and view saved tournaments on Beyond Ball India." path="/player-dashboard" />
      <div className="pt-24 pb-16 min-h-screen">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-10 animate-fade-up">
            <p className="text-gray-500 text-sm mb-1">Welcome back,</p>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900" data-testid="dashboard-title">
              {user?.name} 👋
            </h1>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Profile Card */}
            <div className="lg:col-span-1 animate-fade-up delay-100">
              <div className="bg-white border border-gray-200 rounded-2xl p-6 sticky top-24">
                <div className="flex items-start justify-between mb-4">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#F97316] to-orange-500 flex items-center justify-center text-white font-bold text-xl">
                    {user?.name?.[0]?.toUpperCase()}
                  </div>
                  <Link to="/profile/edit" className="text-gray-400 hover:text-[#F97316] p-2" data-testid="edit-profile-link">
                    <PencilSimple size={18} weight="bold" />
                  </Link>
                </div>
                <h3 className="font-bold text-gray-900 mb-1">{profile?.name || user?.name}</h3>
                <p className="text-sm text-gray-500 mb-4 break-all">{profile?.email || user?.email}</p>

                {profile && (
                  <div className="space-y-2.5 mb-5 text-sm">
                    {profile.city && <div className="flex items-center gap-2 text-gray-700"><MapPin size={14} className="text-gray-400" /> {profile.city}</div>}
                    {profile.current_team && <div className="flex items-center gap-2 text-gray-700"><Trophy size={14} className="text-gray-400" /> {profile.current_team}</div>}
                    {profile.position && <div className="flex items-center gap-2 text-gray-700"><User size={14} className="text-gray-400" /> {profile.position}</div>}
                    {profile.height && <div className="flex items-center gap-2 text-gray-700"><span className="text-gray-400 text-xs">↕</span> {profile.height}</div>}
                    {profile.phone && <div className="flex items-center gap-2 text-gray-700"><Phone size={14} className="text-gray-400" /> {profile.phone}</div>}
                  </div>
                )}

                <div className="pt-4 border-t border-gray-100">
                  <div className="flex justify-between text-xs mb-2">
                    <span className="text-gray-500 font-medium">Profile completion</span>
                    <span className="font-bold text-[#F97316]">{completion}%</span>
                  </div>
                  <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-[#F97316] to-orange-400 transition-all" style={{ width: `${completion}%` }}></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Stats + Lists */}
            <div className="lg:col-span-2 space-y-6">
              {/* Stats */}
              <div className="grid grid-cols-3 gap-4 animate-fade-up delay-200">
                <div className="bg-white border border-gray-200 rounded-2xl p-5">
                  <div className="text-2xl sm:text-3xl font-bold text-gray-900">{registrations.length}</div>
                  <div className="text-xs text-gray-500 uppercase font-semibold mt-1">Registrations</div>
                </div>
                <div className="bg-white border border-gray-200 rounded-2xl p-5">
                  <div className="text-2xl sm:text-3xl font-bold text-green-600">{registrations.filter(r => r.status === 'approved').length}</div>
                  <div className="text-xs text-gray-500 uppercase font-semibold mt-1">Approved</div>
                </div>
                <div className="bg-white border border-gray-200 rounded-2xl p-5">
                  <div className="text-2xl sm:text-3xl font-bold text-[#F97316]">{saved.length}</div>
                  <div className="text-xs text-gray-500 uppercase font-semibold mt-1">Saved</div>
                </div>
              </div>

              {/* Registrations */}
              <div className="bg-white border border-gray-200 rounded-2xl p-6 animate-fade-up delay-300">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="font-bold text-gray-900">My Registrations</h2>
                  <Link to="/tournaments" className="text-xs text-[#F97316] hover:text-orange-700 font-semibold inline-flex items-center gap-1" data-testid="browse-tournaments-cta">
                    Find more <ArrowRight size={12} weight="bold" />
                  </Link>
                </div>
                <ListState
                  loading={loading}
                  empty={registrations.length === 0}
                  skeleton={
                    <div className="space-y-3">
                      {[1,2,3].map(i => <div key={i} className="h-16 rounded-xl skeleton"></div>)}
                    </div>
                  }
                  emptyState={
                    <div className="text-center py-10">
                      <div className="text-3xl mb-2">🏀</div>
                      <p className="text-sm text-gray-500 mb-3">No registrations yet</p>
                      <Link to="/tournaments" className="inline-flex items-center gap-1 text-sm font-semibold text-[#F97316]">Browse tournaments <ArrowRight size={14} weight="bold" /></Link>
                    </div>
                  }
                >
                  <div className="space-y-2">
                    {registrations.map((r, i) => (
                      <div key={r.registration_id} className="flex items-center justify-between p-3.5 rounded-xl hover:bg-gray-50 transition-colors" data-testid={`registration-${i}`}>
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-gray-900 text-sm truncate">{r.tournament_name}</div>
                          <div className="text-xs text-gray-500 mt-0.5">{new Date(r.created_at).toLocaleDateString('en-IN')}</div>
                        </div>
                        <span className={`text-[10px] px-2.5 py-1 rounded-full border font-semibold uppercase ${statusBadge(r.status)}`} data-testid={`registration-status-${i}`}>
                          {statusLabel(r.status)}
                        </span>
                      </div>
                    ))}
                  </div>
                </ListState>
              </div>

              {/* Saved */}
              <div className="bg-white border border-gray-200 rounded-2xl p-6 animate-fade-up delay-400">
                <h2 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Heart size={18} weight="fill" className="text-[#F97316]" /> Saved Tournaments
                </h2>
                {saved.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-6">No saved tournaments. Tap the heart on any tournament to save it.</p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {saved.map((t, i) => (
                      <Link key={t.tournament_id} to={`/tournaments/${t.tournament_id}`} className="block p-3.5 rounded-xl border border-gray-200 hover:border-[#F97316] transition-colors" data-testid={`saved-tournament-${i}`}>
                        <div className="font-semibold text-gray-900 text-sm mb-1 line-clamp-1">{t.name}</div>
                        <div className="text-xs text-gray-500 flex items-center gap-1"><MapPin size={12} /> {t.city}</div>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PlayerDashboard;
